import { useRef, useState } from 'react'
import { Paperclip, X, FileText, Image as ImageIcon, FileArchive, Upload, Loader2 } from 'lucide-react'
import { cn } from '../atoms'
import { useUploadAttachment, type UploadedAttachment } from '../../hooks/useUploadAttachment'
import { getAssetUrl } from '../../utils/assetUrl'

const MAX_SIZE = 25 * 1024 * 1024 // 25MB

function pickIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return ImageIcon
  if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('7z')) return FileArchive
  return FileText
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

export interface AttachmentPickerProps {
  value: UploadedAttachment[]
  onChange: (next: UploadedAttachment[]) => void
  disabled?: boolean
  /** Mensaje opcional del botón */
  label?: string
  className?: string
}

export function AttachmentPicker({ value, onChange, disabled, label = 'Adjuntar archivos', className }: AttachmentPickerProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const upload = useUploadAttachment()

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    setError(null)
    const valid: File[] = []
    for (const f of Array.from(files)) {
      if (f.size > MAX_SIZE) {
        setError(`"${f.name}" supera el límite de 25MB`)
        continue
      }
      valid.push(f)
    }
    if (valid.length === 0) return

    // Subir en paralelo
    const results = await Promise.allSettled(valid.map(f => upload.mutateAsync(f)))
    const uploaded: UploadedAttachment[] = []
    results.forEach((r, i) => {
      if (r.status === 'fulfilled') {
        uploaded.push(r.value)
      } else {
        setError(`Error subiendo "${valid[i].name}": ${r.reason.message}`)
      }
    })
    onChange([...value, ...uploaded])
  }

  const remove = (id: string) => {
    onChange(value.filter(a => a.id !== id))
  }

  return (
    <div className={cn('space-y-2', className)}>
      {/* Botón de selección */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || upload.isPending}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          {upload.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          ) : (
            <Paperclip className="w-4 h-4" aria-hidden="true" />
          )}
          <span>{label}</span>
        </button>
        <span className="text-xs text-gray-500">
          {value.length > 0 ? `${value.length} adjunto${value.length === 1 ? '' : 's'}` : 'Máx 25MB por archivo'}
        </span>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={e => {
            handleFiles(e.target.files)
            if (fileInputRef.current) fileInputRef.current.value = ''
          }}
          aria-label="Seleccionar archivos adjuntos"
        />
      </div>

      {/* Lista de adjuntos */}
      {value.length > 0 && (
        <ul className="space-y-1.5" role="list">
          {value.map(att => {
            const Icon = pickIcon(att.mimeType)
            return (
              <li
                key={att.id}
                className="flex items-center gap-2 p-2 bg-gray-50 border border-gray-200 rounded-lg text-sm"
              >
                <Icon className="w-4 h-4 text-gray-500 flex-shrink-0" aria-hidden="true" />
                <a
                  href={getAssetUrl(att.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 min-w-0 truncate text-gray-900 hover:text-primary-600 hover:underline"
                  title={att.filename}
                >
                  {att.filename}
                </a>
                <span className="text-xs text-gray-500 flex-shrink-0">{formatSize(att.size)}</span>
                <button
                  type="button"
                  onClick={() => remove(att.id)}
                  disabled={disabled}
                  className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 disabled:opacity-50"
                  aria-label={`Quitar ${att.filename}`}
                >
                  <X className="w-3.5 h-3.5" aria-hidden="true" />
                </button>
              </li>
            )
          })}
        </ul>
      )}

      {/* Error */}
      {error && (
        <p role="alert" className="text-xs text-red-600">
          {error}
        </p>
      )}

      {/* Loading indicator durante upload en paralelo */}
      {upload.isPending && (
        <p className="text-xs text-gray-500 inline-flex items-center gap-1">
          <Upload className="w-3 h-3 animate-pulse" aria-hidden="true" />
          Subiendo archivos...
        </p>
      )}
    </div>
  )
}
